import { proxy, subscribe, useSnapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { assert } from '@/utils/assert'

import type { Cue } from '~shared/rundown/cue'
import type { Rundown } from '~shared/rundown/rundown'
import { buildParentGroupMap, fastDeepEqual } from '~shared/utils'

import { getRundown, rundownStoreReadonly } from './data/rundown.store'

import type { Snapshot } from 'valtio'

// =============== TYPES

type RundownDerived = {
  /**
   * Child cue id to the id of its parent group. Root-level cues are absent.
   * See `buildParentGroupMap`.
   */
  parentGroupMap: Record<Cue['id'], Cue['id']>
}

const initialState: RundownDerived = {
  parentGroupMap: {},
}

// =============== STORE

/**
 * Derived values of the rundown document.
 *
 * The document lives in `rundownStore`. Anything computed from it that more
 * than one component needs is computed once here, instead of in each
 * component.
 *
 * @remarks
 *   Read-only outside this module: it is recomputed from the source store, so
 *   any write would be overwritten by the next snapshot.
 *
 * @privateRemarks
 *
 *   ### Why is there no reset function?
 *
 *   Clearing the source store notifies this one automatically, so a dedicated
 *   reset/clear function is not needed.
 *
 *   ### Why this is NOT using `computed` from `valtio-reactive`:
 *
 *   See the same note in `cue.derived.ts`: explicit subscriptions cannot be
 *   silently broken by module evaluation order.
 */
export const rundownDerived = proxy<RundownDerived>(deepClone(initialState))

/**
 * Rebuild every derived value from the rundown document.
 */
function recompute() {
  assignIfChanged(
    'parentGroupMap',
    buildParentGroupMap(getRundown()?.cues ?? []),
  )
}

/**
 * Write `next` only if it differs from what is stored.
 *
 * Every recompute builds a fresh object, so assigning unconditionally would
 * notify subscribers on every rundown change, eg. a rename.
 */
function assignIfChanged<K extends keyof RundownDerived>(
  key: K,
  next: RundownDerived[K],
) {
  if (fastDeepEqual(rundownDerived[key], next)) return

  rundownDerived[key] = next
}

/**
 * Batched (default behaviour), like the cue and column derivations. A
 * synchronous read right after a mutation sees the previous value until the
 * microtask runs.
 */
subscribe(rundownStoreReadonly, recompute)

// The store may already hold a document by the time this module is first imported.
recompute()

// =============== HOOKS

/**
 * The rundown document on the page, or null when no rundown is loaded.
 *
 * @remarks
 *   Only for components hosted outside `RundownRoute`, eg. global modals in the
 *   `ModalHost`. Those can stay mounted after a rundown switch clears the
 *   store, so they must return null while this is null. Everywhere else use
 *   `useRundown`.
 */
export function useRundownOrNull(): Snapshot<Rundown> | null {
  return useSnapshot(rundownStoreReadonly).doc ?? null
}

/**
 * The rundown document on the page.
 *
 * @remarks
 *   Only call from inside a rendered rundown: the rundown route renders its
 *   children after the session has loaded the document. Throws otherwise.
 *   Valtio tracks the fields a component reads, so it only re-renders when
 *   those fields change. Read the fields you need from the returned object
 *   instead of passing the whole snapshot down.
 */
export function useRundown(): Snapshot<Rundown> {
  const rundown = useRundownOrNull()

  assert(rundown, 'useRundown: no rundown is loaded')
  return rundown
}

/**
 * The id of the rundown on the page.
 *
 * @remarks
 *   Only call from inside a rendered rundown, see `useRundown`.
 */
export function useRundownId(): string {
  return useRundown().id
}

/**
 * The settings of the rundown on the page.
 *
 * Only re-renders when `settings` changes, not on any other field.
 *
 * @remarks
 *   Only call from inside a rendered rundown, see `useRundown`.
 */
export function useRundownSettings(): Snapshot<Rundown['settings']> {
  return useRundown().settings
}

/**
 * Subscribe to the parent group map.
 *
 * Only re-renders when the map changes, eg. a cue moves into or out of a group.
 */
export function useParentGroupMap(): Snapshot<Record<Cue['id'], Cue['id']>> {
  return useSnapshot(rundownDerived).parentGroupMap
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(rundownDerived, { name: 'rundownDerived', enabled: true })
}
// #endregion
